import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { caseUtils, objectUtils } from '@codametrix/ui-common';
import classNames from 'classnames';
const truncateOptions = {
    length: 55,
    separator: ' '
};
const prop = (caseRun, propName, optDefault = 'N/A')=>{
    return objectUtils.get(caseRun, propName, optDefault);
};
const isCaseRunList = (content)=>{
    const [firstCaseRun] = content;
    if (`service_line` in firstCaseRun) {
        return true;
    }
    return false;
};
export const filterCaseList = (data)=>{
    const { content } = data;
    if (isCaseRunList(content)) {
        return content;
    }
    return content.filter((item)=>{
        return item.id !== null && item.caseRuns.length > 0;
    }).map(caseUtils.getCaseRun);
};
export const dateOfService = {
    headerName: 'SERVICE DATE',
    field: 'dateOfService',
    sortable: true,
    sort: 'desc',
    valueGetter: (params)=>{
        const dateOfService = prop(params.data, 'date_of_service');
        return dateOfService;
    }
};
export const accessionNumber = {
    field: 'accessionNumber',
    headerName: 'ACCESSION',
    sortable: false,
    valueGetter: (params)=>{
        const primaryClinicalIdentifier = prop(params.data, 'primary_clinical_identifier.number');
        return primaryClinicalIdentifier;
    },
    cellRenderer: (params)=>{
        return /*#__PURE__*/ _jsx("span", {
            className: "sensitive-data",
            children: /*#__PURE__*/ _jsx("span", {
                children: params.value
            })
        });
    }
};
export const pointOfCare = {
    field: 'pointOfCare',
    headerName: 'POINT OF CARE',
    sortable: false,
    cellRenderer: (params)=>{
        const pointOfCareRaw = prop(params.data, 'point_of_care');
        const pointOfCare = objectUtils.truncate(pointOfCareRaw, truncateOptions.length, truncateOptions.separator);
        return /*#__PURE__*/ _jsx("span", {
            className: "case-encounter sensitive-data",
            children: /*#__PURE__*/ _jsx("span", {
                className: "encounter-description primary-info",
                title: pointOfCareRaw,
                children: pointOfCare
            })
        });
    }
};
export const tenantName = {
    field: 'tenant_name',
    headerName: 'LOCATION',
    sortable: false,
    cellRenderer: (params)=>{
        const tenantNameRaw = prop(params.data, 'tenant_name');
        const tenantName = objectUtils.truncate(tenantNameRaw, truncateOptions.length, truncateOptions.separator);
        return /*#__PURE__*/ _jsx("span", {
            className: "case-encounter sensitive-data",
            children: /*#__PURE__*/ _jsx("span", {
                className: "encounter-description primary-info",
                title: tenantNameRaw,
                children: tenantName
            })
        });
    }
};
export const patientMRN = {
    field: 'patientMRN',
    headerName: 'PATIENT MRN',
    sortable: false,
    cellRenderer: (params)=>{
        const patientMRN = prop(params.data, 'patient_identifier.number');
        const patientMRNIssuer = prop(params.data, 'patient_identifier.issuer', 'SRC N/A');
        return /*#__PURE__*/ _jsx("span", {
            className: "case-patient",
            children: /*#__PURE__*/ _jsx("span", {
                className: "patient-mrn primary-info",
                children: /*#__PURE__*/ _jsxs("span", {
                    className: "gap-right",
                    children: [
                        patientMRNIssuer,
                        ":",
                        ' ',
                        /*#__PURE__*/ _jsx("span", {
                            className: "sensitive-data",
                            children: patientMRN
                        })
                    ]
                })
            })
        });
    }
};
export const patientDOB = {
    field: 'patientDOB',
    headerName: 'PATIENT DOB',
    sortable: false,
    cellRenderer: (params)=>{
        const patientDOB = prop(params.data, 'date_of_birth');
        return /*#__PURE__*/ _jsx("span", {
            className: "case-patient",
            children: /*#__PURE__*/ _jsx("span", {
                className: "patient-mrn primary-info",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "gap-right sensitive-data",
                    children: patientDOB
                })
            })
        });
    }
};
export const providerName = {
    field: 'providerName',
    headerName: `SERVICE PROVIDER`,
    sortable: false,
    cellRenderer: (params)=>{
        const billingProviderName = prop(params.data, 'billing_provider_name');
        return /*#__PURE__*/ _jsx("div", {
            className: "case-provider",
            children: /*#__PURE__*/ _jsx("span", {
                className: "case-provider-name primary-info sensitive-data",
                children: billingProviderName
            })
        });
    }
};
export const caseStatus = {
    field: 'caseStatus',
    headerName: `STATUS`,
    sortable: false,
    cellRenderer: (params)=>{
        const status = caseUtils.deriveStatus(params.data);
        const reason = caseUtils.getStatusReason(params.data);
        let classes;
        classes = [
            'case-status',
            status.styleClass,
            {
                'case-has-reason': !!reason
            }
        ];
        let tooltipText;
        tooltipText = objectUtils.isString(reason) ? `${status.signifier}: ${reason}` : `${status.signifier} case`;
        const statusEl = /*#__PURE__*/ _jsx("span", {
            className: "tooltip",
            style: {
                height: 20
            },
            "data-tooltip": tooltipText,
            children: /*#__PURE__*/ _jsxs("span", {
                className: "info-display",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "circle"
                    }),
                    status.signifier
                ]
            })
        });
        return /*#__PURE__*/ _jsx("div", {
            className: classNames(classes),
            children: /*#__PURE__*/ _jsx("span", {
                className: "graphic",
                children: statusEl
            })
        });
    }
};
export const orderNumber = {
    field: 'orderNumber',
    headerName: 'ORDER NUMBER',
    sortable: false,
    cellRenderer: (params)=>{
        const orderNumber = prop(params.data, 'order_number.number', prop(params.data, 'placer_order_number'));
        return /*#__PURE__*/ _jsx("span", {
            className: "sensitive-data",
            children: orderNumber
        });
    }
};
// IPC
export const facility = {
    field: 'facility',
    headerName: 'FACILITY',
    sortable: false,
    valueGetter: (params)=>{
        const facility = prop(params.data, 'billing_location') || 'N/A';
        return facility;
    }
};
export const hospitalAccountRecord = {
    field: 'hospitalAccountRecord',
    headerName: 'HOSPITAL ACCOUNT RECORD',
    sortable: false,
    cellRenderer: (params)=>{
        const hospitalAccountRecord = prop(params.data, 'hospital_account_record');
        return /*#__PURE__*/ _jsx("span", {
            className: "sensitive-data",
            children: hospitalAccountRecord
        });
    }
};
export const noteFinalSignProvider = {
    field: 'noteFinalSignProvider',
    headerName: 'NOTE FINAL SIGN PROVIDER',
    sortable: false,
    valueGetter: (params)=>{
        const noteFinalSignProvider = prop(params.data, 'billing_provider_name') || 'N/A';
        return noteFinalSignProvider;
    }
};
export const noteSpecialtyDesignation = {
    field: 'noteSpecialtyDesignation',
    headerName: 'NOTE SPECIALTY DESIGNATION',
    sortable: false,
    valueGetter: (params)=>{
        const noteSpecialtyDesignation = prop(params.data, 'note_specialty_designation') || 'N/A';
        return noteSpecialtyDesignation;
    }
};
export const defaultColDefs = [
    dateOfService,
    accessionNumber,
    caseStatus,
    patientMRN,
    patientDOB,
    providerName,
    tenantName,
    pointOfCare
];
export const radiologyColDefs = [
    dateOfService,
    accessionNumber,
    orderNumber,
    caseStatus,
    patientMRN,
    patientDOB,
    providerName,
    tenantName,
    pointOfCare
];
export const pathologyColDefs = [
    dateOfService,
    accessionNumber,
    caseStatus,
    patientMRN,
    patientDOB,
    tenantName,
    pointOfCare
];
export const pathologyColDefsOrderNumber = [
    dateOfService,
    accessionNumber,
    orderNumber,
    caseStatus,
    patientMRN,
    patientDOB,
    tenantName,
    pointOfCare
];
export const surgColDefs = [
    dateOfService,
    {
        ...accessionNumber,
        headerName: 'OpTime Case ID'
    },
    caseStatus,
    patientMRN,
    patientDOB,
    tenantName
];
export const automateColDefs = [
    dateOfService,
    facility,
    patientMRN,
    caseStatus,
    patientDOB,
    hospitalAccountRecord,
    noteFinalSignProvider,
    noteSpecialtyDesignation
];
